// T-1 统一接口层
import axios from 'axios';

// 后端基地址（契约 api.md：所有接口前缀 /api）
// 开发：.env.development 里 VUE_APP_API_BASE=http://localhost:9090（直连后端，走 CORS）
// 生产：.env.production 里 VUE_APP_API_BASE 为空 → 同源相对路径，由 nginx 把 /api 反代到后端
export const BASE_URL = process.env.VUE_APP_API_BASE || '';

// 错误码 -> 中文提示（消费 M0 errors.md 错误码表）
export const ERROR_CODE_MESSAGES = {
  FMT_UNSUPPORTED: '该文件格式暂不在支持范围',
  PAIR_UNSUPPORTED: '当前源格式 → 目标格式的组合暂未开放',
  FILE_CORRUPT: '文件已损坏或无法解析，请重新导出后再试',
  FILE_ENCRYPTED: '文件已加密，请提供密码后重试',
  DRM_PROTECTED: '受 DRM 保护，暂不支持转换',
  FILE_TOO_LARGE: '文件超出大小 / 像素 / 条目上限，请压缩后重试',
  ENGINE_TIMEOUT: '转换引擎超时，请稍后重试',
  FONT_MISSING: '缺少所需字体，请补充字体后重试',
  QUALITY_LOSS_WARN: '存在有损或维度跨越，请确认后继续',
  SECURITY_REJECTED: '安全拦截：文件被拒绝（可能为可执行 / 脚本或压缩炸弹）',
  CONVERT_FAILED: '转换失败，请重试'
};

// 把任意异常归一化为 { message, hint, code }，供页面友好展示
export function normalizeError(error) {
  if (!error) return { message: '未知错误', hint: '', code: null };

  const data = (error.response && error.response.data) || null;
  let message = '';
  let hint = '';
  let code = null;

  if (data && typeof data === 'object') {
    code = data.code || error.code || null;
    message = data.msg || data.message || '';
    hint = data.hint || (data.detail ? JSON.stringify(data.detail) : '') || '';
  }

  if (!message && code && ERROR_CODE_MESSAGES[code]) {
    message = ERROR_CODE_MESSAGES[code];
  }
  if (!message) {
    message = error.message || '请求失败，请稍后重试';
  }

  // 未连接到后端
  if (!data && /Network Error|ECONNREFUSED|Failed to fetch/i.test(error.message || '')) {
    message = '无法连接后端服务，请确认服务已启动';
  }

  return { message, hint, code };
}

const request = axios.create({
  baseURL: BASE_URL,
  timeout: 120000,
  withCredentials: true // 携带 HttpOnly Cookie ceanx_session（契约 account.md 第 5 章）
});

request.interceptors.response.use(
  (response) => {
    // 二进制响应（如下载文件）原样返回，交由调用方处理 blob
    if (response.config.responseType === 'blob') {
      return response;
    }
    const res = response.data;
    // 非标准结构（纯数组 / 字符串 / 无 code 字段）直接返回
    if (res === null || typeof res !== 'object' || res.code === undefined) {
      return res;
    }
    if (res.code === 200) {
      return res; // 解包：调用方拿到的就是 { code, msg, data }
    }
    // 业务错误：构造带 code/hint 的异常
    const err = new Error(res.msg || res.message || '请求失败');
    err.backend = true;
    err.code = res.code;
    err.hint = res.hint || (res.detail ? JSON.stringify(res.detail) : '');
    err.response = response;
    return Promise.reject(err);
  },
  (error) => {
    const wrapped = new Error(error.message || '网络异常');
    wrapped.backend = true;
    wrapped.code = error.response && error.response.status;
    wrapped.response = error.response;

    const resp = error.response;
    if (resp && resp.data) {
      const inner = resp.data.data; // { needLogin:true } / { plans:[...] }
      const url = (resp.config && resp.config.url) || '';
      // /api/auth/* 自身的 401/409 由对应弹窗自行处理，不在此全局触发
      const isAuthSelf = /\/api\/auth\//.test(url);
      // 网页版免登录、免费：不再弹登录 / 订阅框，仅打标记交由页面提示
      if (resp.status === 401 && inner && inner.needLogin && !isAuthSelf) {
        wrapped.needLogin = true;
      }
      if (resp.status === 402) {
        wrapped.needPlan = true;
        wrapped.plans = inner && inner.plans ? inner.plans : null;
      }
    }
    return Promise.reject(wrapped);
  }
);

// 触发浏览器下载（blob 落盘），文件名做了中文 / 特殊字符兼容
export function triggerDownload(blob, filename) {
  let safeName = filename || 'download';
  try {
    safeName = decodeURIComponent(safeName);
  } catch (e) {
    // 文件名包含非法的 % 序列时退回原始字符串
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export default request;
